/**
 * BudgetTracker — Inline budget summary bar for itinerary view.
 * Aggregates costs from hotels, meals, activities, and transport across all days.
 * Shows progress bar against user's budget preference, category breakdown,
 * per-day cost chart, and smart suggestions.
 *
 * Supports user-overridden costs via `_userCost` field on items.
 * Users can click any cost in the expanded view to edit it.
 */
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronUp, Pencil } from 'lucide-react';
import { currencySymbol, getCurrencyForCity, approximateToUSD } from '../utils/currencyUtils';

// Budget tier ranges (per person per day, approximate)
const BUDGET_RANGES = {
  budget:    { label: 'Budget',   min: 50,  max: 100, perDay: 75 },
  modest:    { label: 'Modest',   min: 80,  max: 150, perDay: 115 },
  moderate:  { label: 'Moderate', min: 120, max: 250, perDay: 185 },
  comfort:   { label: 'Comfort',  min: 200, max: 400, perDay: 300 },
  luxury:    { label: 'Luxury',   min: 350, max: 800, perDay: 575 },
  ultra:     { label: 'Ultra',    min: 600, max: 2000, perDay: 1000 },
};

/**
 * Parse a cost string to a number. Handles: "$25", "20", "$$", "120", etc.
 * Prioritizes _userCost if present.
 */
function parseCost(val) {
  if (!val && val !== 0) return 0;
  if (typeof val === 'number') return val;
  const str = String(val).trim();
  if (/^\$\$\$\$$/.test(str)) return 80;
  if (/^\$\$\$$/.test(str)) return 50;
  if (/^\$\$$/.test(str)) return 25;
  if (/^\$$/.test(str)) return 12;
  const match = str.match(/[\d,.]+/);
  if (match) {
    const num = parseFloat(match[0].replace(/,/g, ''));
    if (!isNaN(num)) return num;
  }
  return 0;
}

/** Get cost for an item, preferring user override */
function getItemCost(item, ...fallbackFields) {
  if (item?._userCost != null) return parseCost(item._userCost);
  for (const field of fallbackFields) {
    if (item?.[field] != null) return parseCost(item[field]);
  }
  return 0;
}

/**
 * Aggregate costs from itinerary days, collecting editable item refs.
 */
function computeBudget(days, displayCurrency) {
  const perDay = [];
  const editableItems = []; // { dayIdx, itemIdx, itemType, label, cost, isUserSet, currency }
  let totalHotels = 0, totalMeals = 0, totalActivities = 0, totalTransport = 0;
  let hotelNights = 0, mealCount = 0, activityCount = 0;

  for (let dayIdx = 0; dayIdx < (days || []).length; dayIdx++) {
    const day = days[dayIdx];
    if (!day || day._generationFailed) continue;

    // Resolve local currency for this day's destination
    const dayCurrency = day._dailyEstimatedCostCurrency
      || getCurrencyForCity(day.baseCity || day.city || '')
      || displayCurrency || 'USD';
    const sym = currencySymbol(dayCurrency);

    let dayHotel = 0, dayMeals = 0, dayActivities = 0, dayTransport = 0;

    // Hotel
    if (day.hotel?.name) {
      dayHotel = getItemCost(day.hotel, 'pricePerNight', 'estimatedPrice');
      hotelNights++;
      editableItems.push({
        dayIdx, itemIdx: 0, itemType: 'hotel',
        label: `D${day.day} ${day.hotel.name}`,
        cost: dayHotel, isUserSet: day.hotel._userCost != null, currency: dayCurrency, symbol: sym,
      });
    }

    // Meals
    for (let i = 0; i < (day.meals || []).length; i++) {
      const meal = day.meals[i];
      const cost = getItemCost(meal, 'estimatedCost', 'priceRange');
      dayMeals += cost;
      if (cost > 0 || meal._userCost != null) mealCount++;
      editableItems.push({
        dayIdx, itemIdx: i, itemType: 'meal',
        label: `D${day.day} ${meal.restaurant || meal.name || meal.meal || 'Meal'}`,
        cost, isUserSet: meal._userCost != null, currency: dayCurrency, symbol: sym,
      });
    }

    // Schedule items
    for (let i = 0; i < (day.schedule || []).length; i++) {
      const item = day.schedule[i];
      const cost = getItemCost(item, 'estimatedCost', 'cost', 'entryFee', 'priceTier');
      const type = (item.type || '').toLowerCase();
      const isTransport = ['transport', 'transit', 'flight', 'train', 'ferry'].includes(type);
      if (isTransport) {
        dayTransport += cost;
        editableItems.push({
          dayIdx, itemIdx: i, itemType: 'transport',
          label: `D${day.day} ${item.activity || item.title || item.type || 'Transport'}`,
          cost, isUserSet: item._userCost != null, currency: dayCurrency, symbol: sym,
        });
      } else {
        dayActivities += cost;
        if (cost > 0 || item._userCost != null) activityCount++;
        editableItems.push({
          dayIdx, itemIdx: i, itemType: 'activity',
          label: `D${day.day} ${item.activity || item.title || 'Activity'}`,
          cost, isUserSet: item._userCost != null, currency: dayCurrency, symbol: sym,
        });
      }
    }

    // InterCityTransport
    if (day.interCityTransport) {
      const tCost = getItemCost(day.interCityTransport, 'estimatedCost', 'cost');
      dayTransport += tCost;
      editableItems.push({
        dayIdx, itemIdx: -1, itemType: 'transport',
        label: `D${day.day} ${day.interCityTransport.mode || 'Inter-city'} -> ${day.interCityTransport.to || ''}`,
        cost: tCost, isUserSet: day.interCityTransport._userCost != null, currency: dayCurrency, symbol: sym,
      });
    }

    // Convert day totals to USD for cross-currency aggregation
    const toUSD = (amt) => approximateToUSD(amt, dayCurrency);
    totalHotels += toUSD(dayHotel);
    totalMeals += toUSD(dayMeals);
    totalActivities += toUSD(dayActivities);
    totalTransport += toUSD(dayTransport);

    perDay.push({
      day: day.day, baseCity: day.baseCity, currency: dayCurrency, symbol: sym,
      hotel: dayHotel, meals: dayMeals, activities: dayActivities, transport: dayTransport,
      total: dayHotel + dayMeals + dayActivities + dayTransport,
      totalUSD: toUSD(dayHotel) + toUSD(dayMeals) + toUSD(dayActivities) + toUSD(dayTransport),
    });
  }

  const grandTotal = totalHotels + totalMeals + totalActivities + totalTransport;

  return {
    perDay, editableItems,
    // totals are in USD for consistent cross-currency comparison
    totals: { hotels: totalHotels, meals: totalMeals, activities: totalActivities, transport: totalTransport, grand: grandTotal },
    counts: { hotelNights, meals: mealCount, activities: activityCount },
  };
}

// Arrivo brand palette
const CATEGORY_COLORS = {
  hotel: '#313143',
  meals: '#CD7A4D',
  activities: '#1d8e85',
  transport: '#0d9488',
};

/** Inline cost editor — click to edit, blur/enter to save */
function EditableCost({ value, isUserSet, onSave, symbol = '$' }) {
  const [editing, setEditing] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEdit = (e) => {
    e.stopPropagation();
    setInputVal(value > 0 ? String(Math.round(value)) : '');
    setEditing(true);
  };

  const save = () => {
    setEditing(false);
    const num = parseFloat(inputVal);
    if (!isNaN(num) && num >= 0) {
      onSave(String(num));
    } else if (inputVal.trim() === '') {
      onSave(''); // clear override
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        min="0"
        step="1"
        value={inputVal}
        onChange={e => setInputVal(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
        className="w-16 text-right text-sm font-medium border border-[#a7d8db] rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-[#1d8e85] bg-white"
        onClick={e => e.stopPropagation()}
      />
    );
  }

  return (
    <button
      onClick={startEdit}
      className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-[#1d8e85] transition-colors group"
      title="Click to edit cost"
    >
      <span className={isUserSet ? 'text-[#1d8e85]' : ''}>
        {symbol}{Math.round(value).toLocaleString()}
      </span>
      <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-60 transition-opacity" />
    </button>
  );
}

export default function BudgetTracker({ itinerary, budgetPreference, totalDays, onUpdateCost, customBudgetAmount, displayCurrency }) {
  const [expanded, setExpanded] = useState(false);
  const [showAllItems, setShowAllItems] = useState(false);

  const budget = useMemo(() => computeBudget(itinerary?.days, displayCurrency), [itinerary?.days, displayCurrency]);
  const { totals, perDay, counts, editableItems } = budget;

  if (totals.grand === 0 && editableItems.length === 0) return null;

  const pref = BUDGET_RANGES[budgetPreference] || BUDGET_RANGES.moderate;
  const numDays = totalDays || itinerary?.days?.length || perDay.length || 1;
  // Custom budget: user-entered total, otherwise tier x days (budget ranges are in USD)
  const customAmt = customBudgetAmount ? parseFloat(customBudgetAmount) : 0;
  const isCustom = budgetPreference === 'custom' && customAmt > 0;
  const estimatedBudget = isCustom ? customAmt : pref.perDay * numDays;
  const budgetLabel = isCustom ? 'custom' : pref.label.toLowerCase();
  // totals.grand is in USD (normalized), so comparison with USD-based budget ranges works
  const percent = Math.min(Math.round((totals.grand / estimatedBudget) * 100), 200);
  const isOver = totals.grand > estimatedBudget;
  const isClose = percent > 85 && !isOver;

  const isWayOver = percent > 120;
  const barStyle = isWayOver ? { background: 'linear-gradient(90deg, #EF4444, #F87171)' }
    : isOver ? { background: 'linear-gradient(90deg, #F59E0B, #FBBF24)' }
    : isClose ? { background: 'linear-gradient(90deg, #EAB308, #FDE047)' }
    : { background: 'linear-gradient(90deg, #1d8e85, #14b8a6)' };

  const perDayBudget = estimatedBudget / numDays;
  const isUnrealistic = isCustom && perDayBudget < 20;
  const suggestion = isUnrealistic
    ? `Your budget works out to ~$${Math.round(perDayBudget)}/day — that may be tight for most destinations. You can adjust this anytime in Settings.`
    : isOver
    ? `$${Math.round(totals.grand - estimatedBudget)} over your ${budgetLabel} budget. Consider switching to a cheaper hotel or fewer paid activities.`
    : totals.grand < estimatedBudget * 0.7
    ? `$${Math.round(estimatedBudget - totals.grand)} under budget — room to upgrade your hotel or add a special experience!`
    : null;

  const maxDayCost = Math.max(...perDay.map(d => d.totalUSD || d.total), 1);

  // Items to show in editable list — nonzero or user-set, capped unless expanded
  const visibleItems = editableItems.filter(it => it.cost > 0 || it.isUserSet);
  const displayItems = showAllItems ? visibleItems : visibleItems.slice(0, 8);
  const hasMore = visibleItems.length > 8;

  const handleCostEdit = (item, newCost) => {
    if (onUpdateCost) {
      onUpdateCost(item.dayIdx, item.itemIdx, item.itemType, newCost);
    }
  };

  return (
    <div className="border border-gray-200 rounded-xl bg-white shadow-sm mb-3 overflow-hidden">
      {/* Compact summary bar */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-base">💰</span>
          <div className="text-left">
            <div className="text-sm font-semibold text-gray-900">
              ${Math.round(totals.grand).toLocaleString()}
              <span className="font-normal text-gray-400 text-xs ml-1.5">
                / ~${Math.round(estimatedBudget).toLocaleString()} {budgetLabel} (USD)
              </span>
            </div>
            <div className="flex gap-3 mt-1">
              {[
                { label: 'Hotels', val: totals.hotels, color: CATEGORY_COLORS.hotel },
                { label: 'Meals', val: totals.meals, color: CATEGORY_COLORS.meals },
                { label: 'Activities', val: totals.activities, color: CATEGORY_COLORS.activities },
                ...(totals.transport > 0 ? [{ label: 'Transport', val: totals.transport, color: CATEGORY_COLORS.transport }] : []),
              ].map(c => (
                <span key={c.label} className="flex items-center gap-1 text-[11px] text-gray-400">
                  <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: c.color }} />
                  {c.label === 'Hotels' ? '🏨' : c.label === 'Meals' ? '🍽' : c.label === 'Activities' ? '🎯' : '🚗'} {c.label.split(' (')[0]} ${Math.round(c.val)}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-20 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full" style={{ ...barStyle, width: `${Math.min(percent, 100)}%` }} />
          </div>
          <span className="text-xs text-gray-400">{percent}%</span>
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-4 space-y-4">
          {/* Full progress bar */}
          <div>
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ ...barStyle, width: `${Math.min(percent, 100)}%` }}
              />
            </div>
          </div>

          {/* Per-day stacked bar chart */}
          {perDay.length > 1 && (
            <div>
              <div className="text-xs text-gray-500 font-medium mb-2">Daily cost breakdown</div>
              <div className="flex gap-1 items-end" style={{ height: 64 }}>
                {perDay.map((d, i) => {
                  const scale = d.total / maxDayCost;
                  const h = Math.max(scale * 56, 4);
                  const hRatio = d.hotel / (d.total || 1);
                  const mRatio = d.meals / (d.total || 1);
                  const aRatio = d.activities / (d.total || 1);
                  const tRatio = d.transport / (d.total || 1);
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center" title={`Day ${d.day} (${d.baseCity || ''}): ${d.symbol || '$'}${Math.round(d.total).toLocaleString()}`}>
                      <div className="w-full flex flex-col gap-[1px] justify-end" style={{ height: h }}>
                        {tRatio > 0 && <div style={{ height: `${tRatio * 100}%`, background: CATEGORY_COLORS.transport, borderRadius: i === 0 ? '3px 3px 0 0' : '2px 2px 0 0', minHeight: 2 }} />}
                        {hRatio > 0 && <div style={{ height: `${hRatio * 100}%`, background: CATEGORY_COLORS.hotel, minHeight: 2 }} />}
                        {mRatio > 0 && <div style={{ height: `${mRatio * 100}%`, background: CATEGORY_COLORS.meals, minHeight: 2 }} />}
                        {aRatio > 0 && <div style={{ height: `${aRatio * 100}%`, background: CATEGORY_COLORS.activities, borderRadius: '0 0 2px 2px', minHeight: 2 }} />}
                      </div>
                      <span className="text-[9px] text-gray-400 mt-1">D{d.day}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Editable item list */}
          {onUpdateCost && visibleItems.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs text-gray-500 font-medium">Item costs <span className="text-gray-400 font-normal">(click to edit)</span></div>
              </div>
              <div className="space-y-1 max-h-[240px] overflow-y-auto">
                {displayItems.map((item, i) => (
                  <div key={`${item.dayIdx}-${item.itemType}-${item.itemIdx}-${i}`} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-gray-50 -mx-2">
                    <span className="text-xs text-gray-600 truncate flex-1 mr-3" title={item.label}>{item.label}</span>
                    <EditableCost
                      value={item.cost}
                      isUserSet={item.isUserSet}
                      onSave={(val) => handleCostEdit(item, val)}
                      symbol={item.symbol || '$'}
                    />
                  </div>
                ))}
              </div>
              {hasMore && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowAllItems(!showAllItems); }}
                  className="text-xs text-[#1d8e85] mt-1 hover:underline"
                >
                  {showAllItems ? 'Show less' : `Show all ${visibleItems.length} items...`}
                </button>
              )}
            </div>
          )}

          {/* Category totals */}
          <div className="space-y-1.5">
            {[
              { emoji: '🏨', label: `Hotels (${counts.hotelNights} nights)`, val: totals.hotels, color: CATEGORY_COLORS.hotel },
              { emoji: '🍽️', label: `Meals (${counts.meals})`, val: totals.meals, color: CATEGORY_COLORS.meals },
              { emoji: '🎯', label: `Activities (${counts.activities})`, val: totals.activities, color: CATEGORY_COLORS.activities },
              ...(totals.transport > 0 ? [{ emoji: '🚄', label: 'Transport', val: totals.transport, color: CATEGORY_COLORS.transport }] : []),
            ].map(c => (
              <div key={c.label} className="flex justify-between text-sm">
                <span className="text-gray-500">{c.emoji} {c.label}</span>
                <span className="font-medium text-gray-700">${Math.round(c.val).toLocaleString()}</span>
              </div>
            ))}
            <div className="flex justify-between text-sm pt-2 border-t border-gray-100 font-bold">
              <span className="text-[#1d8e85]">Estimated Total</span>
              <span className="text-[#1d8e85]">${Math.round(totals.grand).toLocaleString()}</span>
            </div>
          </div>

          {/* Smart suggestion */}
          {suggestion && (
            <div className={`rounded-lg px-3 py-2.5 text-xs flex items-start gap-2 ${
              isUnrealistic ? 'bg-amber-50 text-amber-700 border border-amber-200'
              : isOver ? 'bg-red-50 text-red-700 border border-red-200'
              : 'bg-[#f0fdfa] text-[#1d8e85] border border-[#a7d8db]'
            }`}>
              <span className="text-sm">{isUnrealistic ? '💡' : isOver ? '⚠️' : '💡'}</span>
              <span>{suggestion}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
